import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import BaseLeaderboardsPage from './BaseLeaderboardsPage';
import { downscale } from 'src/conf/scale';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import StateObserver, { now, user } from 'src/StateObserver';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import { AsyncScroll } from 'src/game/components/shared/AsyncScroll';
import { LeaderboardsMemberEntry } from './LeaderboardsMemberEntry';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { Match2Timer } from 'src/game/minigame/match2-timestep/components/Match2Timer';
import { LeaderboardsPodiumEntry } from './LeaderboardsPodiumEntry';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import {
  getOngoingWeeklyLeaderboardEvent,
  getWeeklyLeaderboardCommonEventProperties,
} from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.getters';
import {
  WeeklyLeaderboardMemberData,
  getWeeklyLeaderboardEventToClaim,
  getWeeklyLeaderboardRanking,
  runWeeklyleaderboardMatchMaking,
} from '../weeklyLeaderboard.utils';
import {
  weeklyLeaderboardRewards,
  weeklyLeaderboardSchedule,
} from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.ruleset';
import { claimPastWeeklyleaderboards } from '../weeklyLeaderboard.sequence';
import { withLoading } from 'src/lib/utils';
import { track } from 'src/lib/analytics/utils';
import { RewardsTooltip } from 'src/game/components/tooltip/RewardsTooltip';

const assets = {
  bgJoining:
    'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/weekly_leaderboard_joining_bg.png',
  bgLeaderboards:
    'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/weekly_leaderboard_regular.png',
  footer:
    'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/leaderboards_footer.png',
  joiningPodium:
    'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/podium.png',
  chest1: 'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/treasure_1.png',
  chest2: 'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/treasure_2.png',
  chest3: 'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/treasure_3.png',
};

export enum LeaderboardState {
  LOADING = 'loading',
  LEADERBOARD = 'leaderboard',
  CLAIMING = 'claiming',
  FAILURE = 'failure',
  JOINING = 'joining',
}

// Store event to force invalidate the caches when it changes
let lastEventId = '';

export default class LeaderboardsWeeklyPage extends BaseLeaderboardsPage {
  protected header: ImageScaleView;
  protected contentView!: View;

  private _currentState: LeaderboardState = LeaderboardState.LEADERBOARD;
  private scroll:
    | AsyncScroll<WeeklyLeaderboardMemberData, LeaderboardsMemberEntry>
    | undefined;
  private leaderboardContent?: View;
  private joiningContent?: View;
  private message!: LangBitmapFontTextView;
  private bg!: ImageScaleView;
  private podiums!: FlexBoxView;
  private podiumEntries: LeaderboardsPodiumEntry[] = [];
  private stickyView!: LeaderboardsMemberEntry;
  private timer?: Match2Timer;
  private footer!: ImageScaleView;
  private footerMessage!: LangBitmapFontTextView;
  private footerButton!: ButtonScaleViewWithText;
  private footerChestIcon!: ImageScaleView;
  private player: WeeklyLeaderboardMemberData | undefined;
  private playerPosition: number | undefined;
  private scrollHeight = 0;
  private rewardsTooltip!: RewardsTooltip;

  public get currentState(): LeaderboardState {
    return this._currentState;
  }

  constructor(superview: View) {
    super(superview);

    const locale = StateObserver.getLocale();

    this.contentView = new View({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      y: 5,
    });

    this.header = new ImageScaleView({
      superview: this,
      image: `assets/ui/weeklyLeaderboard/popups/popupLeaderboards/subtitle_${locale}.png`,
      width: downscale(540),
      height: downscale(120),
      x: this.style.width / 2,
      centerOnOrigin: true,
      y: 60,
    });

    // Info button
    new ButtonScaleView({
      superview: this,
      ...uiConfig.buttons.info,
      x: 45,
      y: 50,
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD.POPUP_INFO,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        openPopupPromise('popupWeeklyLeaderboardInfo', {});
      },
    });

    this.addFooter();
  }

  private addFooter() {
    const footerHeight = downscale(410);

    this.footer = new ImageScaleView({
      superview: this,
      image: assets.footer,
      width: downscale(1110),
      height: footerHeight,
      zIndex: 1,
      visible: false,
      x: -9,
      y: this.style.height - footerHeight,
    });

    this.footerMessage = new LangBitmapFontTextView({
      superview: this.footer,
      size: 50,
      width: 539,
      height: 86,
      font: 'Body',
      color: '#754111',
      align: 'center',
      wordWrap: true,
      verticalAlign: 'center',
      centerOnOrigin: true,
      x: this.footer.style.width * 0.5,
      y: this.footer.style.height / 2 - 40,
    });

    // Button
    this.footerButton = new ButtonScaleViewWithText({
      superview: this.footer,
      ...uiConfig.buttons.secondary,
      centerOnOrigin: true,
      x: this.footer.style.width * 0.5,
      y: this.footer.style.height - 60,
      fontSize: 40,
      width: 350,
      height: 90,
      font: 'Body',
      localeText: () => i18n('match2.tournament.claim'),
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD._,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.claimPastEvents();
      },
    });

    // Chest Reward icon
    this.footerChestIcon = new ImageScaleView({
      superview: this.footerButton,
      width: 75,
      height: 75,
      scaleMethod: 'contain',
      centerOnOrigin: true,
      x: this.footerButton.style.width / 2 - 100,
      y: this.footerButton.style.height / 2 - 5,
      anchorY: 75,
    });
  }

  public setState(state: LeaderboardState) {
    this._currentState = state;

    // Hide all content views
    this.leaderboardContent?.hide();
    this.joiningContent?.hide();

    // Show content for current state
    switch (state) {
      case LeaderboardState.LOADING:
        this.hideJoining();
        this.hideLeaderboard();
        this.hideFooter();
        break;

      case LeaderboardState.LEADERBOARD:
        this.hideJoining();
        this.showLeaderboard();
        this.hideFooter();
        break;

      case LeaderboardState.CLAIMING:
        this.hideJoining();
        this.showLeaderboard();
        this.showFooter({
          message: i18n('weeklyLeaderboard.weekly.claiming_message', {
            x: this.playerPosition,
          }),
          cta: i18n('match2.tournament.claim'),
          subFeature: Feature.WEEKLY_LEADERBOARD.CLAIM,
          chestIcon: this.playerPosition as 1 | 2 | 3,
        });
        break;

      case LeaderboardState.FAILURE:
        this.hideJoining();
        this.showLeaderboard();
        this.showFooter({
          message: i18n('weeklyLeaderboard.weekly.failure_message', {
            x: this.playerPosition,
          }),
          cta: i18n('weeklyLeaderboard.weekly.cta_try_again'),
          subFeature: Feature.WEEKLY_LEADERBOARD.TRY_AGAIN,
        });
        break;

      case LeaderboardState.JOINING:
        this.showJoining();
        this.hideLeaderboard();
        this.hideFooter();
        break;
    }
  }

  private createLeaderboardContent(): View {
    const content = new View({
      superview: this.contentView,
      width: this.style.width,
      height: this.style.height,
      zIndex: -1,
    });

    this.bg = new ImageScaleView({
      superview: content,
      image: assets.bgLeaderboards,
      width: downscale(1092),
      height: downscale(827),
      x: -3,
    });

    this.scrollHeight = content.style.height - this.bg.style.height - 10;
    this.scroll = new AsyncScroll({
      superview: content,
      rect: {
        x: 0,
        y: this.bg.style.height - 5,
        width: content.style.width,
        height: this.scrollHeight,
      },
      createItem: (superview, index, data) => {
        const entry = new LeaderboardsMemberEntry({
          superview,
          weeklyPage: true,
        });
        entry.on('Close', () => {
          this.emit('Close');
        });

        entry.style.x = (superview.style.width - entry.style.width) * 0.5;
        if ('loading' in data) return entry;

        // Add 4 to index since we're showing players after top 3
        const position = index + 4;
        entry.setup({ ...data, position });
        return entry;
      },
      recycle: true,
      setData: (index, data, view) => {
        if ('loading' in data) return;
        const position = index + 4;
        view.setup({ ...data, position });
      },
    });

    // Attach Player's label to bottom of screen when not directly shown on leaderboard
    this.stickyView = new LeaderboardsMemberEntry({
      x: 1,
      width: this.style.width - 2,
      height: 105,
      weeklyPage: true,
    });

    this.stickyView.updateOpts({
      height: this.stickyView.style.height,
    });

    // Create Flexbox to hold all podiums
    this.podiums = new FlexBoxView({
      superview: content,
      width: content.style.width - 20,
      height: downscale(402),
      x: 10,
      y: downscale(827) - downscale(402) - 50,
      flex: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-evenly',
      },
      zIndex: 1,
    });

    // Create rewards tooltip
    this.rewardsTooltip = new RewardsTooltip({
      superview: content,
      width: 230,
      itemLabelOffsetY: -5,
    });

    // Keep track of all podium entries to manage z-indices
    this.podiumEntries = [];

    // Podium 2, medal, chest and trophy inset
    const podium2ndPlace = new LeaderboardsPodiumEntry({
      superview: this.podiums,
      width: downscale(317),
      height: downscale(356),
      zIndex: 1,
      onRewardClick: this.showRewardsTooltip.bind(this, 1),
    });
    this.podiumEntries[1] = podium2ndPlace;

    // Podium 1, medal, chest and trophy inset
    const podium1stPlace = new LeaderboardsPodiumEntry({
      superview: this.podiums,
      width: downscale(318),
      height: downscale(402),
      zIndex: 2,
      onRewardClick: this.showRewardsTooltip.bind(this, 0),
    });
    this.podiumEntries[0] = podium1stPlace;

    // Podium 3, medal, chest and trophy inset
    const podium3rdPlace = new LeaderboardsPodiumEntry({
      superview: this.podiums,
      width: downscale(317),
      height: downscale(314),
      zIndex: 1,
      onRewardClick: this.showRewardsTooltip.bind(this, 2),
    });
    this.podiumEntries[2] = podium3rdPlace;

    // Remaining time of the event
    this.timer = new Match2Timer({
      superview: content,
      x: content.style.width / 2,
      y: this.header.style.height + 40,
      centerOnOrigin: true,
      zIndex: 1,
    });
    this.timer.addListener('ViewRemoved', () => this.timer?.stopAutoUpdate());

    return content;
  }

  private async showRewardsTooltip(index: number) {
    // Position the tooltip over the specified podium entry
    const parent = this.podiums.getSuperview()!;
    const chest = this.podiumEntries[index].chest;
    const position = chest.getPosition(parent);
    this.rewardsTooltip.updateOpts({
      x: position.x + chest.style.width * 0.5,
      y: position.y + chest.style.height * 0.9,
    });

    // Set rewards for the specified position
    const rewards = weeklyLeaderboardRewards[index];

    void this.rewardsTooltip.showTooltip({
      rewards,
      timeout: 2500,
      key: `${index}`,
    });
  }

  private createJoiningContent(): View {
    const content = new View({
      superview: this.contentView,
      width: this.style.width,
      height: this.style.height,
      backgroundColor: '#204CA2',
    });

    // Add podium image
    new ImageScaleView({
      superview: content,
      image: assets.joiningPodium,
      width: downscale(453),
      height: downscale(468),
      scaleMethod: 'contain',
      x: this.style.width / 2,
      centerOnOrigin: true,
      y: this.style.height / 2 - 50,
      zIndex: 1,
    });

    // Add Try Again message
    this.message = new LangBitmapFontTextView({
      superview: content,
      localeText: () => i18n('weeklyLeaderboard.weekly.joining_message'),
      size: 46,
      width: 400,
      height: 86,
      font: 'Body',
      color: '#FFFFFF',
      align: 'center',
      wordWrap: true,
      verticalAlign: 'center',
      centerOnOrigin: true,
      zIndex: 1,
      x: this.style.width * 0.5,
      y: this.style.height / 2 + 200,
    });

    return content;
  }

  protected onShow = async () => {
    const weeklyLeaderboard = user().weeklyLeaderboard;
    if (!weeklyLeaderboard) {
      this.setState(LeaderboardState.JOINING);
      return;
    }

    const eventToClaim = getWeeklyLeaderboardEventToClaim();
    if (eventToClaim) {
      this.displayRanking(eventToClaim);
      return;
    }

    const ongoingEvent = getOngoingWeeklyLeaderboardEvent(user(), now());
    if (!ongoingEvent) {
      this.setState(LeaderboardState.JOINING);
      return;
    }

    if (!weeklyLeaderboard.entries[ongoingEvent.id]) {
      this.setState(LeaderboardState.JOINING);
      return;
    }

    this.displayRanking(ongoingEvent.id);
  };

  private async displayRanking(eventId: string) {
    this.setState(LeaderboardState.LOADING);
    const hasChangedEvent = eventId !== lastEventId;
    lastEventId = eventId;

    // Invalidate relevant caches when changing event during session
    if (hasChangedEvent) {
      await runWeeklyleaderboardMatchMaking(eventId)?.invalidate();
      await getWeeklyLeaderboardRanking(eventId)?.invalidate();
    }

    // Run matchmaking if needed
    await withLoading(
      async () => {
        return runWeeklyleaderboardMatchMaking(eventId)?.get();
      },
      {
        superview: this,
        feature: Feature.WEEKLY_LEADERBOARD._,
        subFeature: Feature.WEEKLY_LEADERBOARD.MATCHMAKING,
      },
    );

    const ranking = await withLoading(
      async () => {
        const result = await getWeeklyLeaderboardRanking(eventId)?.get();
        return result;
      },
      {
        superview: this,
        feature: Feature.WEEKLY_LEADERBOARD._,
        subFeature: Feature.WEEKLY_LEADERBOARD.LOADING_RANKING,
      },
    );

    // If somehow fails, just close the popup and display an error
    if (!ranking) {
      await openPopupPromise('popupError', {
        message: i18n('error.general.tryAgain'),
        title: i18n('error.general.title'),
      });
      this.emit('Close');
      return;
    }

    this.player = ranking.player;
    this.playerPosition = ranking.playerPosition;

    // Track view only after fetching the ranking
    track('WeeklyLeaderboardView', {
      ...getWeeklyLeaderboardCommonEventProperties(user(), eventId, now()),
      rank: ranking.playerPosition,
      originFeature: this.originFeature,
    });

    if (ranking.ended) {
      if (this.playerPosition > 0 && this.playerPosition <= 3) {
        this.setState(LeaderboardState.CLAIMING);
      } else {
        this.setState(LeaderboardState.FAILURE);
      }
    } else {
      this.setState(LeaderboardState.LEADERBOARD);
    }

    // Only happens if user travel in time to an event that has beenclaimed already
    if (user().weeklyLeaderboard?.entries[eventId].claimedAt) {
      this.showFooter({
        message: 'Event already claimed!',
      });
    }

    this.timer?.setRemainingTimeGetter((now) => {
      const event = weeklyLeaderboardSchedule.findEventByID(eventId);
      if (!event) return 0;
      return Math.max(0, event.end - now);
    });
    this.timer?.autoUpdate();

    for (let i = 0; i < 3; i++) {
      this.podiumEntries[i].setup(i + 1, ranking.top3[i]);
    }

    this.scroll?.setDataProvider(
      async () => {
        if (this.player && this.playerPosition && this.playerPosition > 3) {
          this.scroll?.setStickyItem(this.stickyView, this.playerPosition - 4);
          this.stickyView.setup({
            ...this.player,
            position: this.playerPosition,
          });
        } else {
          this.scroll?.clearStickyItem();
        }
        return ranking.rest;
      },
      {
        feature: Feature.WEEKLY_LEADERBOARD._,
        subFeature: Feature.WEEKLY_LEADERBOARD.GET_FRIENDS,
      },
    );
  }

  private async claimPastEvents() {
    await claimPastWeeklyleaderboards();
    this.onShow();
  }

  private showJoining() {
    if (!this.joiningContent) {
      this.joiningContent = this.createJoiningContent();
    }
    this.joiningContent.show();
  }

  private hideJoining() {
    this.joiningContent?.hide();
  }

  private showLeaderboard() {
    if (!this.leaderboardContent) {
      this.leaderboardContent = this.createLeaderboardContent();
    }
    this.leaderboardContent.show();
  }

  private hideLeaderboard() {
    this.leaderboardContent?.hide();
  }

  private showFooter(args: {
    message: string;
    cta?: string;
    subFeature?: string;
    chestIcon?: 1 | 2 | 3;
  }) {
    // Resize scroll to fit above footer
    this.scroll?.resizeScroll({
      height: this.scrollHeight - this.footer.style.height + 30,
    });

    this.footer.show();
    this.footerMessage.localeText = () => args.message;
    if (args.cta) {
      this.footerButton.show();
      this.footerButton.localeText = () => args.cta!;
    } else {
      this.footerButton.hide();
    }
    this.footerButton.label.updateOpts({ centerOnOrigin: true });
    this.footerButton.setLabelPosition({
      x: this.footerButton.style.width / 2,
      y: this.footerButton.style.height / 2 - 6,
    });
    this.footerButton.tracking = {
      feature: Feature.WEEKLY_LEADERBOARD._,
      $subFeature: args.subFeature ?? '',
    };

    if (args.chestIcon) {
      this.footerChestIcon.show();
      this.footerChestIcon.updateOpts({
        image: assets[`chest${args.chestIcon}`],
        scaleMethod: 'contain',
        centerOnOrigin: true,
      });
      this.footerButton.setLabelPosition({
        x: this.footerButton.style.width / 2 + 30,
      });
    } else {
      this.footerChestIcon.hide();
      this.footerButton.setLabelPosition({
        x: this.footerButton.style.width / 2,
      });
    }
  }

  private hideFooter() {
    // Restore scroll height.
    this.scroll?.resizeScroll({
      height: this.scrollHeight,
    });
    this.footer.hide();
  }
}
